import type { CurrencyCode } from 'pi-kiosk-shared';
import {
  ensureAcceptedCurrenciesForCheckout,
  isCurrencyCode,
  resolveRuntimeDefaultCurrency,
  shouldShowCheckoutCurrencyPicker,
} from 'pi-kiosk-shared';

import { resolveShopCurrency } from '../shop/cart/money.js';

export interface CheckoutCurrencySource {
  readonly currencyAcceptanceMode?: 'SINGLE' | 'MULTI' | null;
  readonly acceptedCurrencies?: readonly string[] | null;
  /** Tenant heal preferred — used when accepted is empty. */
  readonly defaultCurrency?: string | null;
}

/**
 * Resolve charge currency for create-session from SP acceptance + optional sticky pick.
 * Empty accepted falls back via ensureAcceptedCurrenciesForCheckout (preferred → CZK).
 * Sticky only when ⊆ ensured accepted list.
 */
export function resolveCheckoutCreateCurrency(
  source: CheckoutCurrencySource | null | undefined,
  stickySelection?: string | null,
): CurrencyCode {
  const accepted = ensureAcceptedCurrenciesForCheckout(
    source?.acceptedCurrencies,
    source?.defaultCurrency,
  );
  if (stickySelection !== undefined && stickySelection !== null && isCurrencyCode(stickySelection)) {
    if (accepted.includes(stickySelection)) {
      return stickySelection;
    }
  }
  return resolveRuntimeDefaultCurrency(accepted) ?? accepted[0] ?? 'CZK';
}

export function resolveAcceptedCheckoutCurrencies(
  source: CheckoutCurrencySource | null | undefined,
): CurrencyCode[] {
  return ensureAcceptedCurrenciesForCheckout(
    source?.acceptedCurrencies,
    source?.defaultCurrency,
  );
}

export function canShowCheckoutCurrencyPicker(
  source: CheckoutCurrencySource | null | undefined,
): boolean {
  return shouldShowCheckoutCurrencyPicker(
    source?.currencyAcceptanceMode,
    source?.acceptedCurrencies,
  );
}

/**
 * Durable Self-Scan / payment-snapshot currency lock (G1 / AC-11).
 * Does **not** require `?selfScan=1` — `isSelfScanCheckoutSearch('?sessionId=x')`
 * may remain false while this still returns true.
 */
export function isSelfScanCurrencyDurablyLocked(input: {
  readonly collectLockedAt?: string | null;
  readonly selfScanBound?: boolean | null;
  readonly cartLocked?: boolean;
}): boolean {
  if (input.selfScanBound === true) {
    return true;
  }
  if (input.cartLocked === true) {
    return true;
  }
  const collectLockedAt = input.collectLockedAt?.trim() ?? '';
  return collectLockedAt.length > 0;
}

/**
 * Lock currency picker after paymentId / QR / gateway / cart lock / awaiting payment.
 *
 * Self-Scan post-handoff: basket is PAYMENT_LOCKED with frozen unitPrice/lineTotal and
 * `customerCheckoutSessionId` bound — currency-switch recreate via generic createSession
 * would catalog re-price and orphan the binding (MarkPaid amount ≢ snapshot).
 * Prefer lock over recreate (G1 / AC-11).
 *
 * Durable signals (URL-independent):
 * - `collectLockedAt` / `selfScanBound` from GET session
 * - `cartLocked` after payment rails
 * Query parks (`selfScanCheckoutHandoff`) remain for pre-hydrate / remount only.
 */
export function isCheckoutCurrencyLocked(input: {
  readonly paymentId?: string | null;
  readonly hasActiveQr?: boolean;
  readonly hasGateway?: boolean;
  readonly awaitingPaymentOnServer?: boolean;
  readonly cartLocked?: boolean;
  /**
   * True on Self-Scan checkout context (`?selfScan=1` and/or `basketPublicId`).
   * Prefer durable fields when search is stripped to `?sessionId=…` only.
   */
  readonly selfScanCheckoutHandoff?: boolean;
  /** ISO collect/amount snapshot lock from session DTO (durable G1 signal). */
  readonly collectLockedAt?: string | null;
  /** Explicit Self-Scan↔CCS bind when exposed on session GET. */
  readonly selfScanBound?: boolean | null;
  /**
   * Seller-Assisted gateway handoff — frozen staff basket snapshot (plan §7 / G14).
   * Currency-switch recreate would orphan SA bind / amount SoT.
   */
  readonly sellerAssistedCheckout?: boolean;
}): boolean {
  if (input.selfScanCheckoutHandoff === true) {
    return true;
  }
  if (input.sellerAssistedCheckout === true) {
    return true;
  }
  if (
    isSelfScanCurrencyDurablyLocked({
      collectLockedAt: input.collectLockedAt,
      selfScanBound: input.selfScanBound,
      cartLocked: input.cartLocked,
    })
  ) {
    return true;
  }
  const paymentId = input.paymentId?.trim() ?? '';
  if (paymentId.length > 0) {
    return true;
  }
  if (input.hasActiveQr === true || input.hasGateway === true) {
    return true;
  }
  if (input.awaitingPaymentOnServer === true) {
    return true;
  }
  return false;
}

/** Sticky CCS currency for display/format — session wins over sticky pick. */
export function resolveDisplayCurrencyFromSession(
  sessionCurrency: string | null | undefined,
  fallback?: string | null,
): CurrencyCode {
  return resolveShopCurrency(sessionCurrency ?? fallback);
}

const STICKY_KEY_PREFIX = 'rpapp.customer.checkoutCurrency.';

export function readStickyCheckoutCurrency(salesPointId: number): CurrencyCode | null {
  if (typeof window === 'undefined' || salesPointId <= 0) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(`${STICKY_KEY_PREFIX}${salesPointId}`);
    return isCurrencyCode(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStickyCheckoutCurrency(
  salesPointId: number,
  currency: CurrencyCode,
): void {
  if (typeof window === 'undefined' || salesPointId <= 0) {
    return;
  }
  try {
    window.sessionStorage.setItem(`${STICKY_KEY_PREFIX}${salesPointId}`, currency);
  } catch {
    // private mode / quota — ignore
  }
}
